import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, StyleSheet, Pressable, TouchableOpacity } from 'react-native';
import { CheckCircle2, Circle, ChevronDown, ChevronUp } from 'lucide-react-native';
import { useTheme, SPACING, TYPOGRAPHY } from '../../theme';
import { ReadinessMeter, Button, LoadingSpinner } from '../common';
import { useChecklistStore } from '../../store/checklistStore';
import { usePostsStore } from '../../store/postsStore';
import { useUIStore } from '../../store/uiStore';
import { useAuthGateStore } from '../../store/authGateStore';
import { InsuranceChecklist } from '../../types';
import { groupEvidenceForChecklist, type ItemEvidence } from '../../utils/checklistEvidence';
import { ChecklistItemEvidenceList } from '../checklist/ChecklistItemEvidenceList';
import { resolveStructureModelId } from '../../utils/structureModel';
import { CURSOR_POINTER } from '@utils/webStyles';

interface ChecklistPreviewProps {
  /** Fires with the model the preview resolved (the page's brand strip reads it). */
  onChecklistResolved?: (checklist: InsuranceChecklist | null) => void;
  structureId: string;
  /** Checklist id from the route's `?model=` param (insurer deep link). */
  modelId?: string;
}

/**
 * Try-then-save evaluation preview: the signed-out version of the featured
 * checklist (PropertyChecklistSection). A visitor from an insurer link (or
 * any shared property link) sees the resolved preparation model's checklist, ticks what they
 * have already done, and watches the readiness meter move — no account, no
 * writes. Ticks live in uiStore.previewEvaluation (sessionStorage-backed on
 * web); the save CTA opens the auth gate, and after sign-up the structure
 * screen claims the property with this model and seeds the ticks as real
 * progress (see the auto-claim effect in app/structure/[id].tsx).
 */
export function ChecklistPreview({
  structureId,
  modelId,
  onChecklistResolved,
}: ChecklistPreviewProps) {
  const { colors } = useTheme();
  const resolvePreviewChecklist = useChecklistStore((s) => s.resolvePreviewChecklist);

  // The link's model can arrive two ways: as this screen's ?model= param, or
  // stashed in pendingProperty when navigation dropped the param.
  const pending = useUIStore((s) => s.pendingProperty);
  const pendingModel = useUIStore((s) => s.pendingModel);
  const effectiveModelId = resolveStructureModelId({
    structureId,
    modelParam: modelId,
    pendingProperty: pending,
    pendingModel,
  });

  const [checklist, setChecklist] = useState<InsuranceChecklist | null>(null);
  const [isResolving, setIsResolving] = useState(true);
  const [tickedIds, setTickedIds] = useState<Set<string>>(new Set());
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [evidenceByItem, setEvidenceByItem] = useState<Map<string, ItemEvidence>>(new Map());
  const fetchStructureEvidencePosts = usePostsStore((s) => s.fetchStructureEvidencePosts);

  useEffect(() => {
    let cancelled = false;
    setIsResolving(true);
    resolvePreviewChecklist(effectiveModelId)
      .then((resolved) => {
        if (cancelled) return;
        setChecklist(resolved);
        onChecklistResolved?.(resolved);
        // Restore held ticks only when they belong to this structure AND the
        // same model — a link with a different model starts a fresh sheet.
        const held = useUIStore.getState().previewEvaluation;
        setTickedIds(
          resolved && held && held.structureId === structureId && held.checklistId === resolved.id
            ? new Set(held.itemIds)
            : new Set()
        );
      })
      .catch(() => {
        if (!cancelled) setChecklist(null);
      })
      .finally(() => {
        if (!cancelled) setIsResolving(false);
      });
    return () => {
      cancelled = true;
    };
  }, [structureId, effectiveModelId, resolvePreviewChecklist]);

  // Public observations already filed against this structure, so a
  // signed-out visitor can see what "done" looks like before they've ticked
  // anything themselves. fetchStructureEvidencePosts scopes the query to the
  // anonymous viewer (public posts only) when there's no signed-in user.
  useEffect(() => {
    if (!checklist) return;
    let cancelled = false;
    fetchStructureEvidencePosts(structureId)
      .then((posts) => {
        if (!cancelled) setEvidenceByItem(groupEvidenceForChecklist(checklist, posts));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [structureId, checklist, fetchStructureEvidencePosts]);

  const handleToggleExpand = useCallback((itemId: string) => {
    setExpandedItem((current) => (current === itemId ? null : itemId));
  }, []);

  if (isResolving) {
    return <LoadingSpinner size="small" style={styles.loadingRow} color={colors.textSecondary} />;
  }
  // No model resolvable (offline, or an empty checklists collection) — the
  // rest of the property screen still works, so render nothing.
  if (!checklist) return null;

  const persist = (ids: Set<string>, saveRequested: boolean) => {
    useUIStore.getState().setPreviewEvaluation({
      structureId,
      checklistId: checklist.id,
      checklistIsDefault: checklist.isDefault === true,
      totalCount: checklist.items.length,
      itemIds: [...ids],
      saveRequested,
    });
  };

  const toggle = (itemId: string) => {
    setTickedIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      persist(next, useUIStore.getState().previewEvaluation?.saveRequested ?? false);
      return next;
    });
  };

  const handleSave = () => {
    persist(tickedIds, true);
    const returnPath = effectiveModelId
      ? `/structure/${structureId}?model=${encodeURIComponent(effectiveModelId)}`
      : `/structure/${structureId}`;
    // The button says "Create a free account", so the sheet opens on the
    // sign-up form. Opening on Sign In asked a first-time visitor to sign in
    // to an account the same sentence had just offered to create.
    useAuthGateStore.getState().open(() => {}, {
      message: 'Create a free account to save your evaluation',
      initialView: 'sign-up',
      returnPath,
    });
  };

  const modelLabel = checklist.displayName ?? checklist.insurerName;

  return (
    <View style={styles.wrapper} testID="checklist-preview">
      <ReadinessMeter completedCount={tickedIds.size} totalCount={checklist.items.length} />

      <Text style={[styles.modelLine, { color: colors.textSecondary }]}>
        {`${modelLabel} model. Tap what you have already done; nothing is saved yet.`}
      </Text>

      <View style={[styles.items, { borderTopColor: colors.border }]}>
        {checklist.items.map((item) => {
          const done = tickedIds.has(item.id);
          const isExpanded = expandedItem === item.id;
          const evidence = evidenceByItem.get(item.id);
          const evidenceCount = (evidence?.direct.length ?? 0) + (evidence?.related.length ?? 0);
          const Chevron = isExpanded ? ChevronUp : ChevronDown;
          return (
            <View
              key={item.id}
              style={[styles.itemContainer, { borderBottomColor: colors.border }]}>
              <View style={styles.itemHeader}>
                <Pressable
                  onPress={() => toggle(item.id)}
                  testID={`preview-item-${item.id}`}
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked: done }}
                  // RN-web doesn't emit aria-* from accessibilityState (see
                  // OptionsPanel); role=checkbox needs aria-checked in the DOM.
                  aria-checked={done}
                  accessibilityLabel={item.label}
                  style={[styles.itemRow, CURSOR_POINTER]}>
                  {done ? (
                    <CheckCircle2 size={16} color={colors.progress} />
                  ) : (
                    <Circle size={16} color={colors.textTertiary} />
                  )}
                  <Text
                    style={[
                      styles.itemLabel,
                      { color: done ? colors.textSecondary : colors.text },
                      done && styles.itemLabelDone,
                    ]}
                    numberOfLines={2}>
                    {item.label}
                  </Text>
                </Pressable>
                <TouchableOpacity
                  testID={`preview-expand-${item.id}`}
                  onPress={() => handleToggleExpand(item.id)}
                  hitSlop={8}
                  style={styles.chevron}
                  accessibilityRole="button"
                  aria-expanded={isExpanded}
                  accessibilityLabel={`${item.label}, ${isExpanded ? 'collapse' : 'expand'} observations`}>
                  <Chevron size={16} color={colors.textTertiary} />
                </TouchableOpacity>
              </View>
              {isExpanded &&
                (evidenceCount > 0 ? (
                  <ChecklistItemEvidenceList evidence={evidence} currentUserId={undefined} />
                ) : (
                  <Text style={[styles.emptyEvidence, { color: colors.textTertiary }]}>
                    No observations yet.
                  </Text>
                ))}
            </View>
          );
        })}
      </View>

      <Button
        title="Create a free account to save your evaluation"
        variant="primary"
        size="lg"
        fullWidth
        onPress={handleSave}
        testID="preview-save-cta"
      />
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    gap: SPACING.sm,
  },
  loadingRow: {
    padding: SPACING.md,
    alignItems: 'center',
  },
  modelLine: {
    ...TYPOGRAPHY.bodySmall,
  },
  // Hairline rows, same visual language as PropertyChecklistSection.
  items: {
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  itemContainer: {
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  itemHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  itemRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    paddingVertical: SPACING.sm + 2,
  },
  itemLabel: {
    ...TYPOGRAPHY.body,
    flex: 1,
    lineHeight: 18,
  },
  itemLabelDone: {
    textDecorationLine: 'line-through',
    opacity: 0.7,
  },
  chevron: {
    paddingHorizontal: SPACING.xs,
    paddingVertical: SPACING.sm + 2,
  },
  emptyEvidence: {
    ...TYPOGRAPHY.caption,
    paddingBottom: SPACING.sm + 2,
  },
});
